import { useState } from 'react';
import axios from 'axios';
import { Search, BookOpen, Loader2, X, Lightbulb } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SermonIllustrations({ onClose }) {
  const [topic, setTopic] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const findIllustrations = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API}/tools/sermon-illustrations`, { topic: topic.trim() });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to find illustrations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="illustrations-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-[#1B3A6B]">Sermon Illustration Finder</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="illustrations-close-btn">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
          <p className="text-sm text-[#475569] mb-3">Enter a sermon topic to find compelling stories, analogies, and real-world examples.</p>
          <div className="flex gap-2">
            <input
              value={topic}
              onChange={e => setTopic(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && findIllustrations()}
              placeholder="e.g., forgiveness, faith under pressure, generosity"
              className="flex-1 px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B] text-sm"
              data-testid="illustrations-topic-input"
            />
            <button
              onClick={findIllustrations}
              disabled={loading || !topic.trim()}
              className="px-5 py-3 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 hover:bg-[#254B8A]"
              data-testid="illustrations-find-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Find
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {['forgiveness', 'perseverance', 'sacrifice', 'community', 'gratitude', 'hope in suffering'].map(s => (
              <button key={s} onClick={() => setTopic(s)} className="px-3 py-1.5 bg-[#F4F2EC] text-[#475569] rounded-full text-xs hover:bg-[#1B3A6B] hover:text-white transition-colors">
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {error && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">{error}</div>}
          {loading && (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#1B3A6B] mx-auto mb-3" />
              <p className="text-sm text-[#475569]">Finding illustrations for "{topic}"...</p>
            </div>
          )}
          {result && (
            <div className="preview-content prose prose-sm max-w-none" data-testid="illustrations-result">
              <ReactMarkdown>{result.illustrations}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
